import axios from "axios";
import "nprogress/nprogress.css";
import NProgress from "nprogress";
import * as Sentry from "@sentry/react";
import config from "@/config.json";
import { danger, requestErrors } from "@";
import { useAuthStore } from "@/shared/stores/useAuthStore.js";

// Refined loader — snappier minimum, smoother trickle, modern easing curve
NProgress.configure({
  showSpinner: true,
  minimum: 0.15,
  trickleSpeed: 150,
  speed: 500,
  easing: "cubic-bezier(0.16, 1, 0.3, 1)",
});

// Set the base URL for API requests
const apiUrl = config?.apiUrl || import.meta.env.VITE_API_URL;
export const baseURL = apiUrl;

// Create an Axios instance with the base URL
const api = axios.create({
  baseURL: apiUrl,
  timeout: 60000 * 5, // Set a timeout of 5 minutes
});

// Add Authorization header if user is authenticated
api.interceptors.request.use((config) => {
  const { user } = useAuthStore.getState();
  if (user?.token) {
    config.headers.Authorization = `Bearer ${user.token}`;
  }
  return config;
});

// Add interceptor to start NProgress on request start
api.interceptors.request.use((config) => {
  NProgress.start();
  return config;
});

// Add interceptor to handle response and errors
api.interceptors.response.use(
  (response) => {
    // Complete the progress bar when response is received
    NProgress.done();
    return response;
  },
  (error) => {
    const { setUser } = useAuthStore.getState();

    // Complete the progress bar on error
    NProgress.done();

    // Show error notification if requestErrors returns a message
    if (requestErrors(error)) {
      danger(requestErrors(error));
    }

    // Handle specific HTTP errors
    const status = error.response?.status;
    if (status === 401 || status === 403 || status === 419 || status === 503) {
      // Clear user from store and redirect to login
      setUser(null);
      window.location.replace("/");
      return;
    }

    // Capture non-auth errors in Sentry
    Sentry.captureException(error, {
      tags: {
        url: error.config?.url,
        method: error.config?.method,
        status,
      },
    });

    return Promise.reject(error);
  },
);

export default api;
